import { useState } from "react";
import { COLORS, BG_BASE } from "../../utils/colors";
import { ClickableName } from "../common/EmployeeNameLink";
import ProjectNameLink from "../common/ProjectNameLink";

const sampleData = {
  score: 7.3,
  engagementBudget: 285000,
  projectedCost: 214200,
  projectedBilling: 285000,
  blendedMargin: 0.248,
  durationWeeks: 12,
  members: [
    {
      name: "Todd Korando",
      role: "DevOps Engineer",
      hourlyRate: 145,
      billingRate: 195,
      hoursPerWeek: 40,
      assessment: "IN RANGE",
      historicalRates: { min: 130, max: 160, avg: 148, comparable: 150 },
      rateHistory: [
        { engagement: "Mobile Technology Roadmap", client: "River Valley Bank", rate: 140, year: 2022 },
        { engagement: "RESTful Services Development", client: "Midwest Data Systems", rate: 145, year: 2023 },
        { engagement: "Legacy System Modernization", client: "United Medical Group", rate: 138, year: 2021 },
      ],
    },
    {
      name: "Shirley Cortez",
      role: "SQL Developer",
      hourlyRate: 125,
      billingRate: 175,
      hoursPerWeek: 40,
      assessment: "DISCOUNT",
      historicalRates: { min: 120, max: 145, avg: 133, comparable: 140 },
      rateHistory: [
        { engagement: "Operational Excellence Program", client: "CoreTech Systems", rate: 130, year: 2022 },
        { engagement: "Digital Transformation", client: "Cardinal Health", rate: 125, year: 2023 },
      ],
    },
    {
      name: "Michael Monske",
      role: "SRE",
      hourlyRate: 155,
      billingRate: 210,
      hoursPerWeek: 40,
      assessment: "PREMIUM",
      historicalRates: { min: 140, max: 165, avg: 153, comparable: 155 },
      rateHistory: [
        { engagement: "High Availability System Design", client: "Metro Healthcare", rate: 155, year: 2023 },
        { engagement: "Cloud Cost Optimization", client: "Midwest Insurance Group", rate: 150, year: 2023 },
        { engagement: "Implementation of Business Intelligence", client: "Premier Healthcare", rate: 148, year: 2023 },
      ],
    },
  ],
};

function RateRangeBar({ current, min, max, avg }) {
  const range = max - min || 1;
  const currentPct = Math.min(Math.max(((current - min) / range) * 100, 0), 100);
  const avgPct = Math.min(Math.max(((avg - min) / range) * 100, 0), 100);
  return (
    <div style={{ position: "relative", width: "100%", height: 20, marginTop: 4 }}>
      <div style={{ position: "absolute", top: 7, left: 0, right: 0, height: 6, background: BG_BASE, borderRadius: 3 }} />
      <div style={{ position: "absolute", top: 7, left: 0, width: "100%", height: 6, background: `${COLORS.purple}30`, borderRadius: 3 }} />
      <div style={{ position: "absolute", top: 4, left: `${avgPct}%`, width: 2, height: 12, background: COLORS.textMuted, transform: "translateX(-50%)", zIndex: 2 }} />
      <div style={{ position: "absolute", top: 3, left: `${currentPct}%`, width: 14, height: 14, borderRadius: "50%", background: COLORS.amber, transform: "translateX(-50%)", zIndex: 3, border: `2px solid ${BG_BASE}` }} />
      <div style={{ position: "absolute", top: 20, left: 0, fontSize: 9, color: COLORS.textMuted }}>${min}</div>
      <div style={{ position: "absolute", top: 20, right: 0, fontSize: 9, color: COLORS.textMuted }}>${max}</div>
    </div>
  );
}

function AssessmentBadge({ assessment }) {
  const config = {
    DISCOUNT:   { bg: `${COLORS.green}20`,  color: COLORS.green,  label: "DISCOUNT — below market" },
    "IN RANGE": { bg: `${COLORS.purple}20`, color: COLORS.purple, label: "IN RANGE — market rate" },
    PREMIUM:    { bg: `${COLORS.amber}20`,  color: COLORS.amber,  label: "PREMIUM — above market" },
  }[assessment] || { bg: "#ffffff10", color: COLORS.textMuted, label: assessment };
  return (
    <span style={{ padding: "3px 10px", borderRadius: 10, fontSize: 10, fontWeight: 700, background: config.bg, color: config.color, letterSpacing: 0.5 }}>
      {config.label}
    </span>
  );
}

function MemberCard({ member, durationWeeks, onEmployeeClick }) {
  const [expanded, setExpanded] = useState(false);
  const totalCost    = member.hourlyRate  * member.hoursPerWeek * durationWeeks;
  const totalBilling = member.billingRate * member.hoursPerWeek * durationWeeks;
  const margin       = totalBilling > 0 ? ((totalBilling - totalCost) / totalBilling) : 0;

  return (
    <div style={{ background: BG_BASE, borderRadius: 8, border: `1px solid ${COLORS.cardBorder}`, overflow: "hidden", marginBottom: 10 }}>
      <div style={{ padding: "12px 16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              <ClickableName name={member.name} onClick={onEmployeeClick} />
            </div>
            <div style={{ fontSize: 10, color: COLORS.textMuted }}>{member.role}</div>
          </div>
          <AssessmentBadge assessment={member.assessment} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
          {[
            { label: "Cost Rate",   value: `$${member.hourlyRate}/hr`,                   color: COLORS.red },
            { label: "Bill Rate",   value: `$${member.billingRate}/hr`,                  color: COLORS.green },
            { label: "Total Cost",  value: `$${(totalCost / 1000).toFixed(0)}k`,         color: COLORS.text },
            { label: "Margin",      value: `${(margin * 100).toFixed(0)}%`,              color: margin >= 0.25 ? COLORS.green : margin >= 0.15 ? COLORS.amber : COLORS.red },
          ].map((item, i) => (
            <div key={i} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 9, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>{item.label}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: item.color }}>{item.value}</div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: COLORS.textMuted, marginBottom: 2 }}>
            <span>Cost Rate vs Historical Range</span>
            <span>Avg: ${member.historicalRates.avg}</span>
          </div>
          <RateRangeBar current={member.hourlyRate} min={member.historicalRates.min} max={member.historicalRates.max} avg={member.historicalRates.avg} />
          <div style={{ marginTop: 18 }} />
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          style={{ width: "100%", padding: "6px", background: "transparent", border: `1px solid ${COLORS.cardBorder}`, borderRadius: 4, color: COLORS.textMuted, fontSize: 10, cursor: "pointer", textTransform: "uppercase", letterSpacing: 0.5 }}
        >
          {expanded ? "▲ Hide Recent Rate History" : `▼ Recent Rate History (${member.rateHistory.length})`}
        </button>
      </div>

      {expanded && (
        <div style={{ borderTop: `1px solid ${COLORS.cardBorder}`, padding: "10px 16px", background: "rgba(0,0,0,0.3)" }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Recent Rate History</div>
          {member.rateHistory.map((h, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: i < member.rateHistory.length - 1 ? `1px solid ${COLORS.cardBorder}30` : "none" }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.text }}>{h.client || h.engagement}</div>
                <div style={{ fontSize: 10, color: COLORS.textMuted }}>
                  <ProjectNameLink project={{ name: h.engagement || h.project_name }}>
                    {h.engagement || h.project_name}
                  </ProjectNameLink>
                </div>
                {(h.start_date || h.end_date || h.year) && (
                  <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 1 }}>
                    {h.start_date && h.end_date
                      ? `${h.start_date} – ${h.end_date}`
                      : h.start_date || h.end_date || (h.year ? String(h.year) : '')}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: h.rate > member.historicalRates.avg ? COLORS.amber : COLORS.green }}>${h.rate}/hr</span>
                <span style={{ fontSize: 9, color: COLORS.textMuted }}>{h.rate > member.billingRate ? "↑" : h.rate < member.billingRate ? "↓" : "="} vs now</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FinancialView({ data, onEmployeeClick }) {
  const d = data || sampleData;
  const scoreColor = d.score >= 7 ? COLORS.green : d.score >= 5 ? COLORS.amber : COLORS.red;
  const marginPct  = d.blendedMargin != null ? (d.blendedMargin * 100).toFixed(1) : null;

  return (
    <div style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, margin: 0, textTransform: "uppercase", letterSpacing: 1 }}>Financial Analysis</h3>
        <span style={{ fontSize: 22, fontWeight: 700, color: scoreColor }}>{d.score}/10</span>
      </div>

      {/* Summary stats */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
        {[
          { label: "Projected Cost",    value: d.projectedCost    != null ? `$${(d.projectedCost    / 1000).toFixed(0)}k` : "—", color: COLORS.red },
          { label: "Projected Billing", value: d.projectedBilling != null ? `$${(d.projectedBilling / 1000).toFixed(0)}k` : "—", color: COLORS.green },
          { label: "Blended Margin",    value: marginPct          != null ? `${marginPct}%` : "—",                               color: d.blendedMargin >= 0.25 ? COLORS.green : d.blendedMargin >= 0.15 ? COLORS.amber : COLORS.red },
          { label: "Budget Fit",        value: d.engagementBudget && d.projectedCost ? (d.projectedCost <= d.engagementBudget ? "✓ On Budget" : "⚠ Over") : "—", color: d.projectedCost <= d.engagementBudget ? COLORS.green : COLORS.red },
        ].map((item, i) => (
          <div key={i} style={{ background: BG_BASE, borderRadius: 6, padding: "10px 12px" }}>
            <div style={{ fontSize: 9, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1 }}>{item.label}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: item.color, marginTop: 4 }}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* Cost / Gross Profit breakdown bar */}
      {d.projectedBilling > 0 && d.projectedCost > 0 && (() => {
        const billing  = d.projectedBilling;
        const cost     = Math.min(d.projectedCost, billing);
        const gp       = billing - cost;
        const costPct  = (cost / billing) * 100;
        const gpPct    = 100 - costPct;
        const margin   = gp / billing;
        const gpColor  = margin >= 0.25 ? COLORS.green : margin >= 0.15 ? COLORS.amber : COLORS.red;
        return (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
              Cost / Gross Profit Breakdown
            </div>
            <div style={{ display: "flex", width: "100%", height: 14, borderRadius: 4, overflow: "hidden" }}>
              <div style={{ width: `${costPct}%`, height: "100%", background: COLORS.red, transition: "width 0.3s" }} />
              <div style={{ width: `${gpPct}%`, height: "100%", background: gpColor, transition: "width 0.3s" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
              <span style={{ fontSize: 10, color: COLORS.red }}>
                Cost ${(cost / 1000).toFixed(0)}k ({costPct.toFixed(0)}%)
              </span>
              <span style={{ fontSize: 10, color: gpColor }}>
                GP ${(gp / 1000).toFixed(0)}k ({gpPct.toFixed(0)}%)
              </span>
            </div>
          </div>
        );
      })()}

      {/* Member cards */}
      <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Member Rate Analysis</div>
      {(d.members || []).map((m, i) => (
        <MemberCard key={i} member={m} durationWeeks={d.durationWeeks || 12} onEmployeeClick={onEmployeeClick} />
      ))}
    </div>
  );
}
